import { useState } from 'react'
import ScrollReveal from '../components/marketing/ScrollReveal'
import PaywallModal from '../components/PaywallModal'
import { setTheme } from '../lib/theme'

// ─── Design tokens ───
const t = {
  bg: '#F5F2EC',
  bgDark: '#252019',
  text: '#1A1714',
  textBody: '#6B6560',
  textFaint: '#9E9189',
  textFaintAlt: '#9C9690',
  accent: '#C4622A',
  accentBtn: '#D97645',
  badgeBg: '#F0DDD5',
  cardBg: '#FFFFFF',
  border: '#E0DCD4',
  divider: '#EDE9E3',
  fontMain: '"Geist", system-ui, sans-serif',
} as const

// ═══════════════════════════════════════════════
// REUSABLE COMPONENTS
// ═══════════════════════════════════════════════

function CornerLogo({ color = '#F5F2EC', size = 22 }: { color?: string; size?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <svg width={size} height={size} viewBox="0 0 256 256" fill="none">
        <defs>
          <linearGradient id="pricingLogoGrad" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
            <stop stopColor="#D46A3A"/>
            <stop offset="1" stopColor="#8B3A10"/>
          </linearGradient>
        </defs>
        <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#pricingLogoGrad)" strokeWidth="40" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: 16, fontWeight: 600, color, fontFamily: t.fontMain, letterSpacing: '-0.01em', lineHeight: '20px' }}>Corner</span>
    </div>
  )
}

function SectionBadge({ children }: { children: string }) {
  return (
    <span style={{
      display: 'inline-flex', borderRadius: 100, padding: '6px 14px',
      backgroundColor: t.badgeBg,
    }}>
      <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.accent, fontFamily: t.fontMain, lineHeight: '14px' }}>
        {children}
      </span>
    </span>
  )
}

function CheckRow({ children }: { children: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <span style={{ fontSize: 14, color: t.accentBtn, fontFamily: t.fontMain, fontWeight: 400, lineHeight: '18px', flexShrink: 0 }}>✓</span>
      <span style={{ fontSize: 14, fontWeight: 400, color: t.text, fontFamily: t.fontMain, lineHeight: '150%' }}>{children}</span>
    </div>
  )
}

// ═══════════════════════════════════════════════
// NAVBAR
// ═══════════════════════════════════════════════

function Navbar() {
  return (
    <nav style={{
      width: '100%', height: 60, display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', padding: '0 40px',
      position: 'sticky', top: 0, zIndex: 100,
      backgroundColor: '#F5F2ECE6', borderBottom: `1px solid ${t.border}`,
      backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    }}>
      <a href="/" style={{ textDecoration: 'none' }}>
        <CornerLogo color={t.text} size={22} />
      </a>
      <div style={{ display: 'flex', gap: 32 }}>
        {['Features', 'How it works', 'Pricing', 'FAQ'].map(link => (
          <a key={link} href={link === 'Pricing' ? '/pricing' : link === 'Features' ? '/features' : link === 'How it works' ? '/how-it-works' : `/#${link.toLowerCase().replace(/\s+/g, '-')}`} style={{
            fontSize: 14, fontWeight: 450, color: '#5C5750', textDecoration: 'none',
            fontFamily: t.fontMain, lineHeight: '18px',
          }}>{link}</a>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <a href="/auth/login" style={{ fontSize: 14, fontWeight: 400, color: '#5C5750', textDecoration: 'none', fontFamily: t.fontMain, lineHeight: '18px' }}>Sign in</a>
        <a href="/auth/register" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8, padding: '9px 18px',
          backgroundColor: '#C4622A', textDecoration: 'none',
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#FFFFFF', fontFamily: t.fontMain, lineHeight: '20px' }}>Get started →</span>
        </a>
      </div>
    </nav>
  )
}

// ═══════════════════════════════════════════════
// PRICING HERO
// ═══════════════════════════════════════════════

function PricingHero({ billing, setBilling }: { billing: 'monthly' | 'yearly'; setBilling: (b: 'monthly' | 'yearly') => void }) {
  return (
    <section style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      width: '100%', paddingTop: 80, gap: 20,
    }}>
      <SectionBadge>Simple, Honest Pricing</SectionBadge>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
        <h1 style={{ fontSize: 48, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: '110%', textAlign: 'center', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
          Start free.
        </h1>
        <h1 style={{ fontSize: 48, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: '110%', textAlign: 'center', color: t.text, fontFamily: t.fontMain, margin: 0 }}>
          Pay when it actually saves you time.
        </h1>
      </div>

      <p style={{ fontSize: 16, lineHeight: '160%', textAlign: 'center', color: t.textBody, fontFamily: t.fontMain, margin: 0, maxWidth: 520 }}>
        Corner is free for the basics — forever. Upgrade when documents become a real part of your work, not before.
      </p>

      {/* Billing Toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', borderRadius: 100,
        padding: 4, gap: 0,
        backgroundColor: t.cardBg, border: `1px solid ${t.border}`,
      }}>
        <button
          onClick={() => setBilling('monthly')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 100, padding: '8px 20px', border: 'none', cursor: 'pointer',
            backgroundColor: billing === 'monthly' ? '#3A2E24' : 'transparent',
            fontFamily: t.fontMain,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 500, color: billing === 'monthly' ? '#FFFFFF' : t.textBody, lineHeight: '18px' }}>Monthly</span>
        </button>
        <button
          onClick={() => setBilling('yearly')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '8px 20px', border: 'none', cursor: 'pointer', gap: 6,
            backgroundColor: billing === 'yearly' ? '#3A2E24' : 'transparent', borderRadius: 100,
            fontFamily: t.fontMain,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 500, color: billing === 'yearly' ? '#FFFFFF' : t.textBody, lineHeight: '18px' }}>Yearly</span>
          <span style={{
            display: 'flex', borderRadius: 100, padding: '2px 8px',
            backgroundColor: t.badgeBg,
          }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: t.accent, lineHeight: '14px' }}>Save 36%</span>
          </span>
        </button>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// PRICING CARDS
// ═══════════════════════════════════════════════

function PricingCards({ billing, selectedPlan, onSelectPlan }: { billing: 'monthly' | 'yearly'; selectedPlan: 'free' | 'pro' | 'business'; onSelectPlan: (p: 'free' | 'pro' | 'business') => void }) {
  const proPrice = billing === 'yearly' ? '$99' : '$12.99'
  const bizPrice = billing === 'yearly' ? '$299' : '$39.99'
  const paidSuffix = billing === 'yearly' ? '/year' : '/month'

  const selectedBorder = `2px solid ${t.accentBtn}`
  const defaultBorder = `1px solid ${t.border}`
  const selectedShadow = '0px 8px 32px #D976451F, 0px 2px 8px #0000000F'
  const defaultShadow = '0px 1px 4px #0000000A'

  const cardBase = (plan: 'free' | 'pro' | 'business') => ({
    display: 'flex' as const, flexDirection: 'column' as const,
    width: 380, flexShrink: 0, borderRadius: 16,
    padding: '36px 32px', gap: 20,
    backgroundColor: t.cardBg,
    fontFamily: t.fontMain,
    border: selectedPlan === plan ? selectedBorder : defaultBorder,
    boxShadow: selectedPlan === plan ? selectedShadow : defaultShadow,
    cursor: 'pointer' as const,
    transition: 'border 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1), transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: selectedPlan === plan ? 'scale(1.015)' : 'scale(1)',
  })

  const tierLabel = { fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.textFaint, lineHeight: '14px' }
  const priceStyle = { fontSize: 52, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: '100%', color: t.text }
  const priceSuffix = { fontSize: 15, fontWeight: 400, color: t.textFaint, lineHeight: '18px' }
  const subtitle = { fontSize: 14, fontWeight: 500, lineHeight: '150%', color: t.textBody }
  const dividerStyle = { width: '100%', height: 1, flexShrink: 0, opacity: 0.6, backgroundColor: t.divider }
  const descStyle = { fontSize: 14, fontWeight: 400, lineHeight: '160%', color: t.textBody }
  const bestForLabel = { fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.textFaint, lineHeight: '14px' }
  const bestForText = { fontSize: 13, fontWeight: 400, lineHeight: '160%', color: t.textBody }

  const ctaButton = (plan: 'free' | 'pro' | 'business', label: string) => {
    const isSelected = selectedPlan === plan
    const handleClick = () => {
      if (plan === 'free') {
        window.location.href = '/auth/register'
      } else {
        // Redirect to register with plan info — after signup, App.tsx will auto-trigger checkout
        const stripePlan = plan === 'business' ? 'enterprise' : plan
        window.location.href = `/auth/register?upgrade=${stripePlan}&cycle=${billing}`
      }
    }
    return (
      <button onClick={handleClick} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '100%', borderRadius: 12, padding: '14px 0',
        border: isSelected ? 'none' : `1px solid ${t.border}`,
        backgroundColor: isSelected ? t.accentBtn : 'transparent',
        cursor: 'pointer', fontFamily: t.fontMain,
        transition: 'background-color 0.35s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        <span style={{ fontSize: 15, fontWeight: isSelected ? 600 : 500, color: isSelected ? '#FFFFFF' : t.text, lineHeight: '18px', transition: 'color 0.35s cubic-bezier(0.4, 0, 0.2, 1)' }}>{label}</span>
      </button>
    )
  }

  return (
    <section style={{
      display: 'flex', alignItems: 'stretch', justifyContent: 'center',
      width: '100%', padding: '40px 80px', gap: 24,
    }}>
      {/* FREE */}
      <div style={cardBase('free')} onClick={() => onSelectPlan('free')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={tierLabel}>Free</span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={priceStyle}>$0</span>
            <span style={priceSuffix}>/forever</span>
          </div>
          <span style={subtitle}>No credit card. No trial. Free forever.</span>
        </div>
        <div style={dividerStyle} />
        <p style={descStyle}>For the occasional document — not your daily workflow. Perfect for students, researchers, or anyone who needs to review a contract, compress a file, or summarize a report every now and then.</p>
        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 10 }}>
          <CheckRow>7 AI conversations / month</CheckRow>
          <CheckRow>3 files per conversation (up to 75 MB)</CheckRow>
          <CheckRow>20 file operations / month</CheckRow>
          <CheckRow>E-sign up to 5 documents / month</CheckRow>
          <CheckRow>7-day conversation history</CheckRow>
        </div>
        {ctaButton('free', 'Get started free')}
      </div>

      {/* PRO */}
      <div style={cardBase('pro')} onClick={() => onSelectPlan('pro')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={tierLabel}>Pro</span>
            <span style={{
              display: 'flex', borderRadius: 100, padding: '3px 10px',
              backgroundColor: t.accentBtn,
            }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#FFFFFF', lineHeight: '14px' }}>Most popular</span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={priceStyle}>{proPrice}</span>
            <span style={priceSuffix}>{paidSuffix}</span>
          </div>
          <span style={subtitle}>14-day free trial. No credit card required.</span>
        </div>
        <div style={dividerStyle} />
        <p style={descStyle}>For solo professionals who live in documents. Whether you're drafting contracts, reviewing agreements, or closing deals — Corner works as hard as you do.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={bestForLabel}>Built for</span>
          <span style={bestForText}>Freelancers, independent lawyers, consultants, founders, and anyone who handles documents daily.</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 10 }}>
          <CheckRow>Unlimited AI conversations</CheckRow>
          <CheckRow>Up to 10 files per conversation (up to 500 MB)</CheckRow>
          <CheckRow>Unlimited file operations</CheckRow>
          <CheckRow>Unlimited e-signatures + full audit trail</CheckRow>
          <CheckRow>Gmail attachment picker</CheckRow>
          <CheckRow>Full conversation history + AI analysis export (PDF)</CheckRow>
          <CheckRow>Priority processing + dedicated support</CheckRow>
        </div>
        {ctaButton('pro', 'Start Pro free for 14 days →')}
      </div>

      {/* ENTERPRISE */}
      <div style={cardBase('business')} onClick={() => onSelectPlan('business')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={tierLabel}>Enterprise</span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={priceStyle}>{bizPrice}</span>
            <span style={priceSuffix}>{paidSuffix}</span>
          </div>
          <span style={subtitle}>Everything in Pro, built for teams.</span>
        </div>
        <div style={dividerStyle} />
        <p style={descStyle}>For small teams and offices where documents are the backbone of every deal, matter, or operation. Stop passing files back and forth — work together, in one place, from start to finish.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={bestForLabel}>Built for</span>
          <span style={bestForText}>Real estate teams, small law firms, medical offices, and ops-heavy startups who share document workflows daily.</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 10 }}>
          <CheckRow>Everything in Pro</CheckRow>
          <CheckRow>Up to 7 team seats — $2.99 per additional seat</CheckRow>
          <CheckRow>Deal & matter workspaces</CheckRow>
          <CheckRow>Gmail, Drive + Dropbox integrations</CheckRow>
          <CheckRow>Workflow automation + bulk processing</CheckRow>
          <CheckRow>Multi-party e-sign with tracking</CheckRow>
          <CheckRow>Compliance-ready audit trail</CheckRow>
          <CheckRow>Dedicated onboarding + priority support</CheckRow>
        </div>
        {ctaButton('business', 'Start Enterprise free for 14 days →')}
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// USED BY STRIP
// ═══════════════════════════════════════════════

function UsedByStrip() {
  const roles = ['Independent Professionals', 'Real Estate Teams', 'Founders & Operators', 'Law Firms', 'Medical Offices']
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap',
      width: '100%', height: 120, padding: '40px 80px', gap: 12,
    }}>
      <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: t.textFaintAlt, lineHeight: '16px', marginRight: 6 }}>Used by</span>
      {roles.map(role => (
        <span key={role} style={{
          display: 'flex', borderRadius: 100, padding: '6px 16px',
          backgroundColor: t.bg, border: `1px solid ${t.border}`,
        }}>
          <span style={{ fontSize: 13, fontWeight: 400, color: '#5C5750', lineHeight: '16px' }}>{role}</span>
        </span>
      ))}
    </div>
  )
}

// ═══════════════════════════════════════════════
// WHICH PLAN SECTION
// ═══════════════════════════════════════════════

function ArrowItem({ children }: { children: string }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ fontSize: 13, color: t.textFaint, fontFamily: t.fontMain, lineHeight: '16px', flexShrink: 0 }}>→</span>
      <span style={{ fontSize: 13, fontWeight: 400, lineHeight: '150%', color: t.textBody, fontFamily: t.fontMain }}>{children}</span>
    </div>
  )
}

function WhichPlanSection() {
  const narrativeCard = {
    display: 'flex' as const, flexDirection: 'column' as const,
    flex: '1 1 0%', borderRadius: 16,
    padding: 32, gap: 20, maxWidth: 410,
    backgroundColor: t.cardBg,
    border: `1px solid ${t.border}`,
    boxShadow: '0px 1px 3px #0000000A',
    fontFamily: t.fontMain,
  }

  return (
    <section style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      width: '100%', padding: '100px 80px 60px', gap: 48,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <SectionBadge>Know Your Plan</SectionBadge>
        <h2 style={{ fontSize: 36, fontWeight: 700, letterSpacing: '-0.03em', textAlign: 'center', color: t.text, fontFamily: t.fontMain, margin: 0, lineHeight: '44px' }}>
          Which plan is right for you?
        </h2>
        <p style={{ fontSize: 16, textAlign: 'center', color: t.textBody, fontFamily: t.fontMain, margin: 0, lineHeight: '20px' }}>
          It comes down to one question: how often do you work with documents?
        </p>
      </div>

      {/* Cards Row */}
      <div style={{ display: 'flex', width: 1280, gap: 24, alignItems: 'stretch' }}>
        {/* Free Narrative */}
        <div style={narrativeCard}>
          <span style={{
            display: 'flex', alignSelf: 'flex-start', borderRadius: 100, padding: '6px 14px',
            backgroundColor: t.badgeBg,
          }}>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.accent, lineHeight: '14px' }}>Free Plan</span>
          </span>
          <span style={{ fontSize: 20, fontWeight: 600, lineHeight: '130%', color: t.text }}>"I deal with a document once in a while"</span>
          <p style={{ fontSize: 14, fontWeight: 400, lineHeight: '165%', color: t.textBody, margin: 0 }}>
            You get a lease to review before signing. Or a freelance contract lands in your inbox. Or you need to compress a PDF to email it. It's occasional — not a daily thing.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.textFaint, lineHeight: '14px' }}>This is you if:</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ArrowItem>You're a student who needs to summarize a research paper</ArrowItem>
            <ArrowItem>You sign maybe 5 contracts per year</ArrowItem>
            <ArrowItem>You need to convert or compress a file occasionally</ArrowItem>
            <ArrowItem>One document at a time is totally fine for your needs</ArrowItem>
          </div>
        </div>

        {/* Pro Narrative */}
        <div style={narrativeCard}>
          <span style={{
            display: 'flex', alignSelf: 'flex-start', borderRadius: 100, padding: '6px 14px',
            backgroundColor: t.accentBtn,
          }}>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: '#FFFFFF', lineHeight: '14px' }}>Pro Plan</span>
          </span>
          <span style={{ fontSize: 20, fontWeight: 600, lineHeight: '130%', color: t.text }}>"Documents are part of my actual job"</span>
          <p style={{ fontSize: 14, fontWeight: 400, lineHeight: '165%', color: t.textBody, margin: 0 }}>
            You review contracts, notices, agreements. You copy-paste chunks into ChatGPT, switch windows constantly, lose your train of thought. You've thought "there has to be a better way" more than once.
          </p>
          {/* Quote block */}
          <div style={{
            display: 'flex', flexDirection: 'column', borderRadius: 8,
            padding: 16, gap: 8,
            backgroundColor: t.bg,
            borderLeft: `3px solid ${t.accentBtn}`,
          }}>
            <span style={{ fontSize: 13, fontWeight: 400, lineHeight: '160%', color: t.textBody }}>
              "My current workflow feels dumb. I keep copy-pasting chunks of documents into ChatGPT, switching windows constantly. Death by a thousand Ctrl+C / Ctrl+V cuts."
            </span>
            <span style={{ fontSize: 12, fontWeight: 400, color: t.textFaint, lineHeight: '16px' }}>— r/productivity, 2.5k upvotes</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.textFaint, lineHeight: '14px' }}>This is you if:</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ArrowItem>You review contracts or agreements more than a few times a week</ArrowItem>
            <ArrowItem>You ask iterative AI questions — "review this clause", "redraft in plain English"</ArrowItem>
            <ArrowItem>You need to upload a contract alongside its annexures and cross-reference them</ArrowItem>
          </div>
        </div>

        {/* Business Narrative */}
        <div style={narrativeCard}>
          <span style={{
            display: 'flex', alignSelf: 'flex-start', borderRadius: 100, padding: '6px 14px',
            backgroundColor: t.badgeBg,
          }}>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.accent, lineHeight: '14px' }}>Business Plan</span>
          </span>
          <span style={{ fontSize: 20, fontWeight: 600, lineHeight: '130%', color: t.text }}>"My team runs on documents"</span>
          <p style={{ fontSize: 14, fontWeight: 400, lineHeight: '165%', color: t.textBody, margin: 0 }}>
            You're managing multiple deals simultaneously. Your team shares document workflows across people and projects. You need visibility, organization, and bulk processing.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.textFaint, lineHeight: '14px' }}>This is you if:</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ArrowItem>You manage multiple deals or matters simultaneously</ArrowItem>
            <ArrowItem>Your team needs shared workspaces and visibility</ArrowItem>
            <ArrowItem>You need bulk processing and workflow automation</ArrowItem>
            <ArrowItem>Compliance and audit trails are non-negotiable</ArrowItem>
          </div>
        </div>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// COMPARE PLANS TABLE
// ═══════════════════════════════════════════════

type CellValue = string | boolean | null // string = text, true = check, null = dash

function ComparePlansSection() {
  const rows: { feature: string; free: CellValue; pro: CellValue; biz: CellValue }[] = [
    { feature: 'Documents', free: '5', pro: 'Unlimited', biz: 'Unlimited' },
    { feature: 'AI questions / month', free: '25', pro: 'Unlimited', biz: 'Unlimited' },
    { feature: 'File uploads', free: true, pro: true, biz: true },
    { feature: 'PDF tools (merge, split, compress)', free: true, pro: true, biz: true },
    { feature: 'E-signatures', free: null, pro: true, biz: true },
    { feature: 'AI document generation', free: null, pro: true, biz: true },
    { feature: 'AI analysis & chat', free: null, pro: true, biz: true },
    { feature: 'Priority support', free: null, pro: null, biz: true },
    { feature: 'Team workspaces', free: null, pro: null, biz: true },
    { feature: 'Bulk processing', free: null, pro: null, biz: true },
    { feature: 'Audit trails', free: null, pro: null, biz: true },
    { feature: 'Custom workflows', free: null, pro: null, biz: true },
  ]

  const headerCell = { display: 'flex' as const, width: 280, height: 56, alignItems: 'center' as const, justifyContent: 'center' as const, flexShrink: 0 }
  const dataCell = { display: 'flex' as const, width: 280, height: 48, alignItems: 'center' as const, justifyContent: 'center' as const, flexShrink: 0 }

  function renderCell(val: CellValue, _isPro: boolean) {
    if (val === null) return <span style={{ fontSize: 14, color: '#C8C4BC', lineHeight: '18px' }}>—</span>
    if (val === true) return <span style={{ fontSize: 14, color: t.accentBtn, lineHeight: '18px' }}>✓</span>
    // text value — "Unlimited" in accent color, numbers in default
    const isHighlight = val === 'Unlimited'
    return <span style={{ fontSize: 14, fontWeight: isHighlight ? 500 : 400, color: isHighlight ? t.accentBtn : t.text, lineHeight: '18px' }}>{val}</span>
  }

  return (
    <section style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      width: '100%', padding: '80px 80px 60px', gap: 48,
      backgroundColor: t.bg,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <SectionBadge>Feature Breakdown</SectionBadge>
        <h2 style={{ fontSize: 40, fontWeight: 700, letterSpacing: '-0.03em', color: t.text, fontFamily: t.fontMain, margin: 0, lineHeight: '48px' }}>
          Compare plans
        </h2>
      </div>

      {/* Table */}
      <div style={{ display: 'flex', flexDirection: 'column', width: 1280, flexShrink: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', width: 1280, height: 56, alignItems: 'center', borderBottom: `1px solid ${t.border}` }}>
          <div style={{ display: 'flex', width: 440, height: 56, alignItems: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: t.textFaint, lineHeight: '14px' }}>Features</span>
          </div>
          <div style={headerCell}>
            <span style={{ fontSize: 14, fontWeight: 600, color: t.text, lineHeight: '18px' }}>Free</span>
          </div>
          <div style={{ ...headerCell, borderTopLeftRadius: 8, borderTopRightRadius: 8, backgroundColor: '#D976450F' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: t.accentBtn, lineHeight: '18px' }}>Pro</span>
          </div>
          <div style={headerCell}>
            <span style={{ fontSize: 14, fontWeight: 600, color: t.text, lineHeight: '18px' }}>Business</span>
          </div>
        </div>

        {/* Rows */}
        {rows.map((row) => (
          <div key={row.feature} style={{ display: 'flex', width: 1280, height: 48, alignItems: 'center', borderBottom: `1px solid ${t.border}` }}>
            <div style={{ display: 'flex', width: 440, height: 48, alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 400, color: t.text, lineHeight: '18px' }}>{row.feature}</span>
            </div>
            <div style={dataCell}>{renderCell(row.free, false)}</div>
            <div style={{ ...dataCell, backgroundColor: '#D976450F' }}>{renderCell(row.pro, true)}</div>
            <div style={dataCell}>{renderCell(row.biz, false)}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// THEME SWITCHER
// ═══════════════════════════════════════════════
type ThemeMode = 'auto' | 'light' | 'dark'

function ThemeSwitcher() {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'auto'
    const stored = localStorage.getItem('corner:theme')
    if (stored === 'light' || stored === 'auto' || stored === 'dark') return stored
    return 'auto'
  })

  const setMode = (m: ThemeMode) => {
    setModeState(m)
    setTheme(m)
  }

  const modes: ThemeMode[] = ['auto', 'light', 'dark']
  const activeIdx = modes.indexOf(mode)
  const indicatorOffset = activeIdx * 30
  const iconColor = (m: ThemeMode) => mode === m ? '#F5F2EC' : '#F5F2EC59'
  const btnStyle = { position: 'relative' as const, zIndex: 1, width: 28, height: 28, borderRadius: 100, border: 'none', cursor: 'pointer', backgroundColor: 'transparent', display: 'flex' as const, alignItems: 'center' as const, justifyContent: 'center' as const, padding: 0 }

  return (
    <div style={{
      position: 'relative',
      display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start',
      borderRadius: 100, padding: 3, gap: 2, marginTop: 4, width: 'fit-content',
      backgroundColor: t.bgDark, border: '1px solid #F5F2EC14',
    }}>
      <div style={{
        position: 'absolute', top: 3, left: 3 + indicatorOffset,
        width: 28, height: 28, borderRadius: 100,
        backgroundColor: '#3A2E24', boxShadow: '#00000080 0px 1px 3px',
        transition: 'left 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: 'none',
      }} />
      <button onClick={() => setMode('auto')} aria-label="System theme" style={btnStyle}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <rect x="1" y="2" width="12" height="8" rx="1.5" stroke={iconColor('auto')} strokeWidth="1.2" fill="none" />
          <line x1="4" y1="12" x2="10" y2="12" stroke={iconColor('auto')} strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>
      <button onClick={() => setMode('light')} aria-label="Light theme" style={btnStyle}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="3" stroke={iconColor('light')} strokeWidth="1.2" fill="none" />
          <line x1="7" y1="0.5" x2="7" y2="2" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
          <line x1="7" y1="12" x2="7" y2="13.5" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
          <line x1="0.5" y1="7" x2="2" y2="7" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
          <line x1="12" y1="7" x2="13.5" y2="7" stroke={iconColor('light')} strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      </button>
      <button onClick={() => setMode('dark')} aria-label="Dark theme" style={btnStyle}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M12 8.5A5.5 5.5 0 1 1 5.5 2 4.5 4.5 0 0 0 12 8.5Z" stroke={iconColor('dark')} strokeWidth="1.2" fill="none" />
        </svg>
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════════
// FOOTER
// ═══════════════════════════════════════════════

function Footer() {
  const columns = [
    { title: 'Product', links: [
      { label: 'Features', href: '/features' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'How it works', href: '/how-it-works' },
      { label: 'Changelog', href: '#' },
    ]},
    { title: 'Tools', links: [
      { label: 'PDF tools', href: '#' },
      { label: 'E-signature', href: '#' },
      { label: 'AI analysis', href: '#' },
      { label: 'File conversion', href: '#' },
    ]},
    { title: 'Company', links: [
      { label: 'About', href: '#' },
      { label: 'Blog', href: '#' },
      { label: 'Privacy', href: '/privacy' },
      { label: 'Terms', href: '#' },
    ]},
  ]

  return (
    <footer style={{
      width: '100%', padding: '56px 170px 40px',
      backgroundColor: t.bgDark,
      borderTop: '1px solid #F5F2EC14',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 48 }}>
        {/* Top row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {/* Left: Logo + tagline */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <CornerLogo color="#F5F2EC" size={22} />
            <p style={{ fontSize: 13, fontWeight: 400, lineHeight: '160%', color: '#F5F2EC59', maxWidth: 200, margin: 0, fontFamily: t.fontMain }}>
              Stop managing documents. Start finishing them.
            </p>
            <ThemeSwitcher />
          </div>

          {/* Right: Link columns */}
          <div style={{ display: 'flex', gap: 72 }}>
            {columns.map(col => (
              <div key={col.title} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, color: '#F5F2EC40', lineHeight: '14px' }}>{col.title}</span>
                {col.links.map(link => (
                  <a key={link.label} href={link.href} style={{ fontSize: 13, fontWeight: 400, color: '#F5F2EC80', textDecoration: 'none', lineHeight: '16px', fontFamily: t.fontMain }}>{link.label}</a>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 24, borderTop: '1px solid #F5F2EC0F' }}>
          <span style={{ fontSize: 12, fontWeight: 400, color: '#F5F2EC33', lineHeight: '16px', fontFamily: t.fontMain }}>© 2026 Corner. All rights reserved.</span>
          <span style={{ fontSize: 12, fontWeight: 400, color: '#F5F2EC33', lineHeight: '16px', fontFamily: t.fontMain }}>trycorner.co</span>
        </div>
      </div>
    </footer>
  )
}

// ═══════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════

export default function PricingPage() {
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly')
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'pro' | 'business'>('pro')
  const [showPaywall, setShowPaywall] = useState(false)

  const handleSelectPlan = (plan: 'free' | 'pro' | 'business') => {
    setSelectedPlan(plan)
    if (plan === 'pro' || plan === 'business') {
      setShowPaywall(true)
    } else {
      window.location.href = '/auth/register'
    }
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      backgroundColor: t.bg, fontFamily: t.fontMain, minHeight: '100vh',
    }}>
      <Navbar />
      <ScrollReveal style={{ width: '100%' }}><PricingHero billing={billing} setBilling={setBilling} /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><PricingCards billing={billing} selectedPlan={selectedPlan} onSelectPlan={handleSelectPlan} /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><UsedByStrip /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><WhichPlanSection /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><ComparePlansSection /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><Footer /></ScrollReveal>
      <PaywallModal
        open={showPaywall}
        plan={selectedPlan === 'business' ? 'business' : 'pro'}
        onClose={() => setShowPaywall(false)}
        onUpgrade={(_plan, _cycle) => {
          setShowPaywall(false)
          window.location.href = '/auth/register'
        }}
      />
    </div>
  )
}
